import React from 'react';
import { Box, Paper, Typography, LinearProgress, Stack, Chip, IconButton, Tooltip } from '@mui/material';
import { RestartAlt as ResetIcon } from '@mui/icons-material';
import { Task } from '@/types';

interface TaskSummaryProps {
  tasks: Task[];
  onFilterByStatus?: (status: string | null) => void;
  activeFilter?: string | null;
}

export const TaskSummary: React.FC<TaskSummaryProps> = ({ 
  tasks,
  onFilterByStatus,
  activeFilter
}) => {
  const stats = {
    total: tasks.length,
    completed: tasks.filter(t => t.status === 'completed').length,
    inProgress: tasks.filter(t => t.status === 'in_process').length,
    notStarted: tasks.filter(t => t.status === 'not_started').length,
    frozen: tasks.filter(t => t.status === 'frozen').length
  };

  const completionRate = stats.total > 0 
    ? Math.round((stats.completed / stats.total) * 100)
    : 0;

  const handleFilterClick = (status: string) => {
    // If clicking the active filter, clear it
    onFilterByStatus?.(activeFilter === status ? null : status);
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Task Progress
      </Typography>

      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="h5" color="text.secondary" fontWeight="normal">
            Completion Rate
          </Typography>
          <Typography variant="h5" color="text.secondary" fontWeight="normal">
            {completionRate}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={completionRate}
          sx={{
            height: 10,
            borderRadius: 5,
            bgcolor: 'action.hover',
            '& .MuiLinearProgress-bar': {
              borderRadius: 5,
              bgcolor: 'primary.main'
            }
          }}
        />
      </Box>

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        <Tooltip title={activeFilter === 'completed' ? 'Click to clear filter' : 'Show completed tasks'}>
          <Chip
            label={`${stats.completed} Completed`}
            color="success"
            onClick={() => handleFilterClick('completed')}
            sx={{ 
              borderRadius: '16px',
              px: 2,
              height: 32,
              fontSize: '1rem',
              cursor: 'pointer',
              borderWidth: 2,
              borderStyle: 'solid',
              borderColor: activeFilter === 'completed' ? 'success.main' : 'transparent',
              '&:hover': {
                borderColor: activeFilter === 'completed' ? 'success.dark' : 'success.main'
              }
            }}
          />
        </Tooltip>

        <Tooltip title={activeFilter === 'in_process' ? 'Click to clear filter' : 'Show in-progress tasks'}>
          <Chip
            label={`${stats.inProgress} In Progress`}
            color="primary"
            onClick={() => handleFilterClick('in_process')}
            sx={{ 
              borderRadius: '16px',
              px: 2,
              height: 32,
              fontSize: '1rem',
              cursor: 'pointer',
              borderWidth: 2,
              borderStyle: 'solid',
              borderColor: activeFilter === 'in_process' ? 'primary.main' : 'transparent',
              '&:hover': {
                borderColor: activeFilter === 'in_process' ? 'primary.dark' : 'primary.main'
              }
            }}
          />
        </Tooltip>

        <Tooltip title={activeFilter === 'not_started' ? 'Click to clear filter' : 'Show not started tasks'}>
          <Chip
            label={`${stats.notStarted} Not Started`}
            onClick={() => handleFilterClick('not_started')}
            sx={{ 
              borderRadius: '16px',
              px: 2,
              height: 32,
              fontSize: '1rem',
              cursor: 'pointer',
              bgcolor: 'grey.200',
              borderWidth: 2,
              borderStyle: 'solid',
              borderColor: activeFilter === 'not_started' ? 'grey.500' : 'transparent',
              '&:hover': {
                bgcolor: 'grey.300',
                borderColor: activeFilter === 'not_started' ? 'grey.600' : 'grey.500'
              }
            }}
          />
        </Tooltip>

        <Tooltip title={activeFilter === 'frozen' ? 'Click to clear filter' : 'Show frozen tasks'}>
          <Chip
            label={`${stats.frozen} Frozen`}
            color="error"
            onClick={() => handleFilterClick('frozen')}
            sx={{ 
              borderRadius: '16px',
              px: 2,
              height: 32,
              fontSize: '1rem',
              cursor: 'pointer',
              borderWidth: 2,
              borderStyle: 'solid',
              borderColor: activeFilter === 'frozen' ? 'error.main' : 'transparent',
              '&:hover': {
                borderColor: activeFilter === 'frozen' ? 'error.dark' : 'error.main'
              }
            }}
          />
        </Tooltip>

        {activeFilter && (
          <Tooltip title="Clear filters">
            <IconButton 
              onClick={() => onFilterByStatus?.(null)}
              size="small"
              color="primary"
              sx={{ ml: 1 }}
            >
              <ResetIcon />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Paper>
  );
};